/*
 * The last line of the link defence (security.md T8, spec 06 §5): the one answer to "may this href
 * go into the DOM".
 *
 * It lives beside the routes because every module that draws a link reaches `routes/links.ts`
 * already, and nothing may reach into another module. A copy per module is how this rule goes
 * wrong: a copy that refuses `//host` easily lets `/\host` through.
 *
 * The scheme is read from a copy with whitespace, control characters and the invisible separators
 * removed, because "java\tscript:" is one string to a browser and another to a naive prefix test.
 */

const ALLOWED_SCHEME = /^(https?:|mailto:)/i
// The control characters are the point: a browser strips them before it reads the scheme, so the
// check must too.
// eslint-disable-next-line no-control-regex
const INVISIBLE = /[\u0000-\u0020\u007f-\u00a0\u200b-\u200f\u2028-\u202f\ufeff]/g

/**
 * The href to put in the DOM, or null when nothing may be linked. An internal path ("/c/dev") is
 * allowed; a protocol-relative "//host" is not — it is a jump to another origin wearing the shape
 * of a path. Neither is "/\host": in the authority position a browser reads a backslash as a
 * slash, so `/\evil.example/x` resolves to `http://evil.example/x`.
 */
export function safeHref(href: string): string | null {
	if (href === "") return null
	const probe = href.replace(INVISIBLE, "")
	if (probe === "") return null
	if (/^\/[/\\]/.test(probe)) return null
	if (probe.startsWith("/")) return href
	if (ALLOWED_SCHEME.test(probe)) return href
	return null
}

/** The same answer, narrowed to a path inside this installation: no scheme and no host at all. */
export function internalHref(href: string | undefined): string | null {
	if (href === undefined) return null
	const safe = safeHref(href)
	return safe !== null && safe.startsWith("/") ? safe : null
}
